/**
 * Tokens do design system Zarya — portados verbatim de `docs/design/zarya-tokens.html`.
 *
 * A fonte da verdade continua sendo aquele arquivo: os valores abaixo são a mesma
 * lista de custom properties, só que tipada, para (a) alimentar o tema MUI e
 * (b) ser injetada como `:root { --token: valor }` por `global.css` — que é o que
 * os componentes portados de `zarya-components.css` consomem via `var(--token)`.
 *
 * Não invente token novo aqui. Se faltar algo, registre a pergunta ao lead.
 */

export type TokenName = keyof typeof light;

/** Tokens que não mudam entre claro e escuro (tipografia, raios, métricas). */
export const staticTokens = {
  font: '"Inter var","Inter","Segoe UI Variable Text","Segoe UI",Roboto,Helvetica,Arial,sans-serif',
  mono: 'ui-monospace,"Cascadia Mono",Consolas,monospace',
  'sidebar-w': '280px',
  'topbar-h': '64px',
  'r-btn': '8px',
  'r-btn-sm': '4px',
  'r-paper': '8px',
  'r-dialog': '16px',
  'r-input': '4px',
} as const;

export const light = {
  primary: '#006B69',
  'primary-active': '#003A32',
  'primary-light': '#DEEAEA',
  'secondary-main': '#0DA08D',
  header: '#034A4C',
  'primary-contrast': '#FFFFFF',

  // Tinta sobre superfícies que NÃO mudam de claridade entre os temas, e que
  // por isso não podem depender de `primary-contrast` (esse é o contraste do
  // `primary`, e no escuro vira tinta escura). Sem override no bloco escuro:
  // o chrome da topbar é escuro nos dois temas, então a tinta é a mesma.
  'on-header': '#FFFFFF',
  'on-header-2': '#DDE7E6',
  'on-accent': '#062220',

  'green-100': '#EFF5F5',
  'green-200': '#DEEAEA',
  'green-300': '#BDD5D5',
  'green-400': '#9CC0C0',
  'green-500': '#7BAAAA',
  'green-600': '#5D9292',
  'green-700': '#487171',
  'green-900': '#1F3030',

  'gray-100': '#FBFBFB',
  'gray-200': '#EAEAEA',
  'gray-300': '#C1C1C1',
  'gray-400': '#979797',
  'gray-500': '#6E6E6E',
  'gray-600': '#4E4E4E',
  'gray-700': '#383838',
  'gray-800': '#212121',
  'gray-900': '#0B0B0B',

  'crit-critical': '#B3001A',
  'crit-high': '#B33700',
  'crit-medium': '#BAA700',
  'crit-low': '#9DB556',
  'crit-minimum': '#2F8451',

  'success-light': '#D0EEDC',
  'success-active': '#2F8451',
  'success-dark': '#112F1D',
  'warning-light': '#FFD3BF',
  'warning-active': '#B33700',
  'warning-dark': '#401400',
  'danger-light': '#FFBFC8',
  'danger-active': '#B3001A',
  'danger-dark': '#400009',
  'info-light': '#D9E9E9',
  'info-dark': '#004037',

  'dv-cyan': '#00C3FF',
  'dv-blue': '#2287FF',
  'dv-teal': '#00D1B5',
  'dv-green': '#3CAA69',
  'dv-yellow': '#FFCA51',
  'dv-orange': '#FF930F',
  'dv-red': '#FF667C',
  'dv-purple': '#BB62F8',

  paper: '#F9F9F9',
  default: '#EFF5F5',
  surface: '#FFFFFF',

  // A rampa de texto para no piso do WCAG AA: sobre `--default` (#EFF5F5),
  // o cinza mais claro que ainda passa 4,5:1 é #6E6E6E. `fg-4` era #979797
  // (2,65:1) e é o token do texto secundário do console inteiro — 91
  // seletores. Descer a rampa até o piso legível é a correção; abaixo dele
  // não existe cinza que passe, e `fg-3` recuou um passo para manter ordem.
  fg: '#0B0B0B',
  'fg-2': '#4E4E4E',
  'fg-3': '#5B5B5B',
  'fg-4': '#6E6E6E',

  divider: '#EAEAEA',
  border: '#BDD5D5',

  shadow: '0 1px 3px rgba(11,11,11,.06), 0 1px 2px rgba(11,11,11,.04)',
} as const;

/** Só os tokens que o bloco escuro de `zarya-tokens.html` redefine. */
const darkOverrides = {
  primary: '#00D1B5',
  'primary-active': '#9AF1DC',
  'primary-light': '#12302E',
  'secondary-main': '#9AF1DC',
  'primary-contrast': '#062220',

  header: '#0A1414',
  paper: '#141E1E',
  default: '#070C0C',
  surface: '#141E1E',

  fg: '#E6EDEC',
  'fg-2': '#A9BDBB',
  'fg-3': '#9BB3B1',
  'fg-4': '#87A19F',

  divider: '#20302F',
  border: '#27403F',

  // A escala verde inverte no escuro (100 vira a mais escura). O bloco
  // escuro de `zarya-tokens.html` esquece `green-900`, que fica #1F3030 —
  // tinta quase preta sobre superfícies verdes escuras. Era o item de menu
  // selecionado, as pills e o contador discreto, todos a 1,0–1,3:1.
  'green-100': '#0F1A1A',
  'green-200': '#12302E',
  'green-300': '#27403F',
  'green-600': '#7BAAAA',
  'green-900': '#D9EAE8',

  // A escala cinza inverte como a verde: 100 é o degrau mais escuro, abaixo do
  // `paper`. Sem override, `dark` herdava o #FBFBFB do claro — e todo inset
  // `--gray-100` ficava branco sob texto claro (Achado 13, 04/09).
  'gray-100': '#111A19',
  'gray-200': '#1C2A29',
  'gray-300': '#33504E',

  'success-light': '#12332A',
  'success-active': '#5DBE8E',
  'success-dark': '#B7E8CD',
  'warning-light': '#3A2412',
  'warning-active': '#E8A06A',
  'warning-dark': '#FFD9C2',
  'danger-light': '#3A1218',
  'danger-active': '#F2818F',
  'danger-dark': '#FFC7CF',
  'info-light': '#12302E',
  'info-dark': '#7FD9CD',

  'crit-critical': '#F2818F',
  'crit-high': '#E8A06A',
  'crit-medium': '#DCC85E',
  'crit-low': '#BBD182',
  'crit-minimum': '#5DBE8E',

  shadow: '0 1px 3px rgba(0,0,0,.4)',
} as const satisfies Partial<Record<TokenName, string>>;

export const dark = { ...light, ...darkOverrides } as Record<TokenName, string>;

export const palettes = { light, dark } as const;
export type ColorMode = keyof typeof palettes;

/** Serializa uma paleta como declarações CSS (`--primary:#006B69;…`). */
export function toCssDeclarations(palette: Record<string, string>): string {
  return Object.entries(palette)
    .map(([name, value]) => `--${name}:${value};`)
    .join('');
}
